import * as React from 'react'
import { cn } from '../../utils/cn'
import { school } from '../../tokens'
import { Crest, type CrestProps } from './crest'

/**
 * JPG Academy School Brand
 *
 * A pre-composed brand lockup: crest + school name.
 * Handles the hierarchy between the full name (John Paul the Great Academy)
 * and the short name (JPG Academy) based on available space.
 *
 * Variants:
 *   - `full` — Crest + "John Paul the Great Academy" + optional motto
 *   - `short` — Crest + "JPG Academy"
 *   - `mark` — Crest only (for tight spaces)
 *
 * The full name always uses Trajan Pro Bold (uppercase, as the typeface demands).
 * The motto uses Garamond italic for classical elegance.
 *
 * Usage:
 *   <SchoolBrand variant="full" />              ← Login page, about page
 *   <SchoolBrand variant="short" />             ← App header, sidebar
 *   <SchoolBrand variant="mark" size="sm" />    ← Favicon, avatar fallback
 */
export type SchoolBrandVariant = 'full' | 'short' | 'mark'

export interface SchoolBrandProps extends React.HTMLAttributes<HTMLDivElement> {
	/** Which brand variant to show */
	variant?: SchoolBrandVariant
	/** Crest size (maps to Crest component sizes) */
	crestSize?: CrestProps['size']
	/** Show the motto below the name (full variant only) */
	showMotto?: boolean
	/** Use horizontal layout (crest left of text) */
	horizontal?: boolean
}

export function SchoolBrand({
	variant = 'short',
	crestSize = 'md',
	showMotto = false,
	horizontal = true,
	className,
	...props
}: SchoolBrandProps) {
	const isHorizontal = horizontal && variant !== 'mark'

	return (
		<div
			className={cn(
				'flex items-center',
				isHorizontal ? 'flex-row gap-3' : 'flex-col items-center gap-2',
				className,
			)}
			{...props}
		>
			{/* Crest */}
			<Crest
				size={crestSize}
				className={cn(
					variant === 'mark' && 'mx-auto',
					variant === 'full' && !isHorizontal && 'mx-auto',
				)}
			/>

			{/* Text */}
			{variant !== 'mark' && (
				<div className={cn(
					'flex flex-col min-w-0',
					!isHorizontal && 'items-center text-center',
				)}>
					{/* Name */}
					<span
						className={cn(
							'font-display uppercase font-bold truncate leading-tight',
							variant === 'full' && 'tracking-trajan-wide text-base sm:text-lg',
							variant === 'short' && 'tracking-trajan-wide text-sm',
						)}
					>
						{variant === 'full' ? school.fullName : school.shortName}
					</span>

					{/* Motto (full variant only) */}
					{variant === 'full' && showMotto && (
						<span className="font-body text-xs text-muted-foreground italic mt-0.5 leading-snug">
							{school.motto}
						</span>
					)}
				</div>
			)}
		</div>
	)
}
